/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { throttle } from 'lodash';

import { Hidden, breakpoints } from '@coursera/cds-core';

import useIsTablet from 'bundles/unified-common/hooks/useIsTablet';
import PageNavigationBase from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';

const styles = {
  container: () => css`
    ${breakpoints.up('md')} {
      border-bottom: 2px solid var(--cds-color-grey-50);
    }
  `,
};

const PageNavigation = ({ ...rest }) => {
  const navRef = useRef<HTMLDivElement | null>(null);
  const previousScrollTop = useRef<number>(0);
  const { updateInPageNavPosition } = usePageNavigation();
  const isTablet = useIsTablet();

  useEffect(() => {
    const updateNavPosition = throttle(() => {
      const top = navRef.current ? navRef.current.getBoundingClientRect().top - navRef.current.offsetHeight : undefined;
      if (top !== undefined && updateInPageNavPosition) {
        if (isTablet) {
          const scrollY = window?.scrollY || 0;
          const isScrollingDown = scrollY >= previousScrollTop.current;
          previousScrollTop.current = scrollY;
          updateInPageNavPosition(top, isScrollingDown);
        } else {
          updateInPageNavPosition(top);
        }
      }
    }, 250);

    updateNavPosition();

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', updateNavPosition);
      window.addEventListener('resize', updateNavPosition);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', updateNavPosition);
        window.removeEventListener('resize', updateNavPosition);
      }
    };
  }, [updateInPageNavPosition, isTablet]);

  return (
    <div ref={navRef} css={styles.container}>
      <Hidden smDown>
        <PageNavigationBase {...rest} />
      </Hidden>
    </div>
  );
};

export default PageNavigation;
